import React from "react";
import {
  $Checkbox,
  $Input,
  $Select,
  $AutoComplete,
  $Button,
  $Space,
} from "us.common/components";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  UndoOutlined,
} from "us.icons";
import { DocumentManagerColumKeys } from "./constants";
import { handleResetMappedDocument } from "./functions/functions";

type DocumentDetailsColumnRenderContext = {
  t: (key: string, options?: any) => string;
  selectedAll: boolean;
  setSelectedAll: (v: boolean) => void;
  setRows: React.Dispatch<React.SetStateAction<any[]>>;
  updateRow: (key: any, changes: Partial<any>) => void;
  editingDocKey: any;
  editingDocName: string;
  setEditingDocName: (v: string) => void;
  startEditingDocName: (rec: any) => void;
  saveDocName: (key: any) => void;
  cancelEditDocName: () => void;
  multipleSelected: boolean;
  EntityTypeOptions: any[];
  documentCategories: any[];
  getEntityData?: (args: {
    assignTo: string;
    entityType: string;
    key: any;
  }) => void;
  submittingKeysRef: React.MutableRefObject<Set<any>>;
  rows: any[];
  handleSingleMap: (rec: any) => void;
  handleAssignToNavigation: (entityType: string, assignTo: string) => void;
  caseAssignmentResult?: any;
  entityInfo?: any;
  assignedDocuments?: (args: { documentIds: number[] }) => void;
  prevUrlRef: React.MutableRefObject<string | null>;
  setPreviewUrl: (url: string | null) => void;
  setPreviewText: (text: string) => void;
};
/**
@description
DocumentDetailsColumnRender Component
Renders columns for the Documents Manager table including selection, document name,
entity type, assigned to and action controls. Manages inline editing, preview trigger,
and per-row actions such as mapping and unmapping.
Features:
- Checkbox selection and select-all handling
- Inline document name editing
- Preview URL creation for files
- Entity type and assign to controls with async lookup
@link https://unicorn-solutions.atlassian.net/browse/UPD-20
@author Keshan Nimnaka <keshana@unicorn-solutions.com>
@since 29/06/2026
*/

export const DocumentDetailsColumnRender = (
  ctx: DocumentDetailsColumnRenderContext,
) => {
  const {
    t,
    selectedAll,
    setSelectedAll,
    setRows,
    updateRow,
    editingDocKey,
    editingDocName,
    setEditingDocName,
    startEditingDocName,
    saveDocName,
    cancelEditDocName,
    multipleSelected,
    EntityTypeOptions,
    documentCategories,
    getEntityData,
    submittingKeysRef,
    rows,
    handleSingleMap,
    handleAssignToNavigation,
    caseAssignmentResult,
    entityInfo,
    assignedDocuments,
    prevUrlRef,
    setPreviewUrl,
    setPreviewText,
  } = ctx;
  const columns = [
    {
      title: (
        <$Checkbox
          checked={selectedAll}
          onChange={(e: any) => {
            const checked = e.target.checked;
            setSelectedAll(checked);

            setRows((prev: any[]) =>
              prev.map((r: any) => (r.mapped ? r : { ...r, checked })),
            );
          }}
        />
      ),
      dataIndex: "checked",
      key: "checked",
      width: 48,
      render: (_: any, record: any) => (
        <$Checkbox
          checked={!!record.checked}
          disabled={!!record.mapped}
          onChange={(e: any) =>
            updateRow(record.key, { checked: e.target.checked })
          }
        />
      ),
    },
    {
      title: t(
        "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.DOCUMENT_NAME",
      ),
      dataIndex: "documentName",
      key: DocumentManagerColumKeys.DOCUMENT_NAME,
      render: (val: string, rec: any) => {
        const isEditing = String(editingDocKey) === String(rec.key);

        if (isEditing) {
          return (
            <div className="home-doc-edit-container">
              <$Input
                autoFocus
                size="small"
                name="documentName"
                value={editingDocName}
                onChange={(e: any) => setEditingDocName(e.target.value)}
                onBlur={() => saveDocName(rec.key)}
                onKeyDown={(e: any) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    saveDocName(rec.key);
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    cancelEditDocName();
                  }
                }}
                className="home-doc-name-input"
              />
            </div>
          );
        }

        return (
          <div
            className={`home-doc-name-wrapper ${rec.mapped ? "home-doc-name-wrapper-mapped" : ""}`}
            onClick={() => {
              if (!rec.mapped) startEditingDocName(rec);
            }}
          >
            <a
              className="home-doc-link"
              onClick={(e: any) => {
                e.stopPropagation();
                if (rec.file) {
                  try {
                    const url = URL.createObjectURL(rec.file as Blob);
                    if (prevUrlRef.current) {
                      URL.revokeObjectURL(prevUrlRef.current);
                    }
                    prevUrlRef.current = url;
                    setPreviewUrl(url);
                    setPreviewText(rec.documentName);
                  } catch (e) {
                    setPreviewUrl(null);
                    setPreviewText(rec.documentName);
                  }
                } else {
                  setPreviewUrl(null);
                  setPreviewText(rec.documentName);
                }
              }}
            >
              <FilePdfOutlined className="home-doc-link-icon" />
              <span>{val}</span>
            </a>
            <div className="home-doc-edit-trigger"></div>
          </div>
        );
      },
    },
    {
      title: t(
        "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.ENTITY_TYPE",
      ),
      dataIndex: DocumentManagerColumKeys.TYPE,
      key: DocumentManagerColumKeys.TYPE,
      width: 125,
      render: (val: string, rec: any) => (
        <$Select
          name="entityType"
          size="small"
          allOption={false}
          className="home-full-width"
          showSearch={false}
          popupClassName="entity-type-dropdown-popup"
          value={rec.entityType ?? ""}
          disabled={multipleSelected || !!rec.mapped}
          options={EntityTypeOptions}
          onChange={(value: any) => {
            if (multipleSelected) return;
            const entityType = String(value ?? "");
            updateRow(rec.key, { entityType, canConfirm: false });
          }}
        />
      ),
    },
    {
      title: t(
        "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.ASSIGNED_TO",
      ),
      dataIndex: DocumentManagerColumKeys.ASSIGNED_TO,
      key: DocumentManagerColumKeys.ASSIGNED_TO,
      width: 120,
      render: (val: string, rec: any) => {
        const submitAssignTo = async (key: string, assignTo: string) => {
          if (multipleSelected) return;
          if (!assignTo) return;
          if (submittingKeysRef.current.has(key)) return;

          const current = rows.find((r: any) => r.key === key);
          const entityType = current?.entityType ?? "";
          if (!entityType) return;

          submittingKeysRef.current.add(key);

          updateRow(key, { loading: true });
          try {
            getEntityData && getEntityData({ assignTo, entityType, key });
          } catch (err) {
            updateRow(key, { loading: false });
          } finally {
            submittingKeysRef.current.delete(key);
          }
        };

        if (rec.mapped) {
          return (
            <a
              className="home-mapped-value"
              onClick={() =>
                handleAssignToNavigation(
                  rec.entityType ?? "",
                  rec.assignTo ?? "",
                )
              }
              style={{ cursor: "pointer" }}
            >
              {rec.assignTo ?? ""}
            </a>
          );
        }

        return (
          <$Input
            name="assignTo"
            size="small"
            value={rec.assignTo ?? ""}
            disabled={multipleSelected}
            onChange={(e: any) =>
              updateRow(rec.key, {
                assignTo: e.target.value,
                canConfirm: false,
              })
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (multipleSelected) return;
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              if (multipleSelected) return;
              submitAssignTo(rec.key, e.target.value);
            }}
          />
        );
      },
    },
    {
      title: t("US.COLLECTION.COMMON:COMMON.CATEGORY"),
      dataIndex: DocumentManagerColumKeys.CATEGORY,
      key: DocumentManagerColumKeys.CATEGORY,
      width: 135,
      render: (val: string, rec: any) =>
        rec.mapped ? (
          <div className="home-mapped-value">{rec.category ?? ""}</div>
        ) : (
          <$AutoComplete
            size="small"
            name="category"
            disabled={multipleSelected}
            className="home-category-autocomplete home-full-width"
            popupClassName="category-autocomplete-popup"
            options={(documentCategories || []).map((c: any) => ({
              value: c.documentCategory,
            }))}
            value={rec.category ?? ""}
            onChange={(value: any) => {
              if (multipleSelected) return;
              const category = String(value ?? "");
              updateRow(rec.key, { category });
            }}
          />
        ),
    },
    {
      title: t("US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.DEBTOR_NAME"),
      dataIndex: DocumentManagerColumKeys.DEBTOR_NAME,
      key: DocumentManagerColumKeys.DEBTOR_NAME,
      render: (val: string, rec: any) => (
        <div>
          <div className="home-name-highlight">{val}</div>
          {rec.errorMessage ? <div>{rec.errorMessage}</div> : null}
        </div>
      ),
    },
    {
      title: t("US.COLLECTION.CREDITOR:CREDITOR_INFO.CREDITOR_NAME"),
      dataIndex: DocumentManagerColumKeys.CREDITOR_NAME,
      key: DocumentManagerColumKeys.CREDITOR_NAME,
      render: (val: string, rec: any) => (
        <div>
          <div className="home-name-highlight">{val}</div>
          {rec.errorMessage ? <div>{rec.errorMessage}</div> : null}
        </div>
      ),
    },
    {
      title: "",
      key: DocumentManagerColumKeys.ACTIONS,
      width: 60,
      render: (_: any, rec: any) => (
        <$Space>
          <$Button
            type="primary"
            size="small"
            name="singleMapButton"
            disabled={!!rec.mapped || !rec.canConfirm || multipleSelected}
            loading={(caseAssignmentResult?.isLoading  || entityInfo?.isLoading || multipleSelected ) && rec.loading}
            icon={
              <CheckCircleOutlined
                className={rec.mapped ? "home-icon-white" : undefined}
              />
            }
            className={rec.mapped ? "home-btn-mapped" : undefined}
            onClick={() => handleSingleMap(rec)}
          ></$Button>
          {!rec.mapped && (
            <$Button
              className="home-action-delete"
              icon={<DeleteOutlined />}
              disabled={!!rec.mapped}
              onClick={() =>
                setRows((prev: any[]) => prev.filter((r) => r.key !== rec.key))
              }
            ></$Button>
          )}
          {rec.mapped && (
            <$Button
              className="home-reset"
              icon={<UndoOutlined />}
              onClick={() =>
                handleResetMappedDocument(
                  caseAssignmentResult,
                  rec,
                  assignedDocuments,
                  setRows,
                )
              }
            ></$Button>
          )}
        </$Space>
      ),
    },
  ];

  return columns;
};

export default DocumentDetailsColumnRender;
